import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';

// Public Pages
import Welcome from './pages/Welcome';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import About from './pages/About';
import HelpCenter from './pages/HelpCenter';
import ContactUs from './pages/ContactUs';
import Documentation from './pages/Documentation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';

// Protected Pages
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import PostProject from './pages/PostProject';
import AdminDashboard from './pages/AdminDashboard';
import ManagerDashboard from './pages/ManagerDashboard';

// New Project Management Pages
import BrowseProjects from './pages/BrowseProjects';
import ProjectDetails from './pages/ProjectDetails';
import ClientProjects from './pages/ClientProjects';
import FreelancerProjects from './pages/FreelancerProjects';
import FreelancerRequests from './pages/FreelancerRequests';
import ProjectRequests from './pages/ProjectRequests';
import Messages from './pages/Messages';
import Notifications from './pages/Notifications';
import ProjectMessaging from './pages/ProjectMessaging';
import PaymentSimulation from './pages/PaymentSimulation';

// Route Guards
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ManagerRoute from './components/ManagerRoute';
import ClientRoute from './components/ClientRoute';
import FreelancerRoute from './components/FreelancerRoute';

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Welcome />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/about" element={<About />} />
          <Route path="/helpcenter" element={<HelpCenter />} />
          <Route path="/contactus" element={<ContactUs />} />
          <Route path="/documentation" element={<Documentation />} />
          <Route path="/privacypolicy" element={<PrivacyPolicy />} />
          <Route path="/termsofservice" element={<TermsOfService />} />
          <Route path="/cookiepolicy" element={<CookiePolicy />} />
          
          {/* Protected Routes - Require Authentication */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/notifications" 
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            } 
          />
          
          {/* Project Management Routes */}
          <Route 
            path="/post-project" 
            element={
              <ClientRoute>
                <PostProject />
              </ClientRoute>
            } 
          />
          
          {/* Client Routes */}
          <Route 
            path="/client/projects" 
            element={
              <ClientRoute>
                <ClientProjects />
              </ClientRoute>
            } 
          />
          <Route 
            path="/client/projects/:projectId" 
            element={
              <ClientRoute>
                <ProjectDetails />
              </ClientRoute>
            } 
          />
          <Route 
            path="/client/projects/:projectId/requests" 
            element={
              <ClientRoute>
                <ProjectRequests />
              </ClientRoute>
            } 
          />
          <Route 
            path="/client/projects/:projectId/messages" 
            element={
              <ClientRoute>
                <ProjectMessaging />
              </ClientRoute>
            } 
          />
          <Route 
            path="/client/projects/:projectId/payment" 
            element={
              <ClientRoute>
                <PaymentSimulation />
              </ClientRoute>
            } 
          />
          
          {/* Freelancer Routes */}
          <Route 
            path="/projects" 
            element={
              <FreelancerRoute>
                <BrowseProjects />
              </FreelancerRoute>
            } 
          />
          <Route 
            path="/projects/:projectId" 
            element={
              <FreelancerRoute>
                <ProjectDetails />
              </FreelancerRoute>
            } 
          />
          <Route 
            path="/freelancer/projects" 
            element={
              <FreelancerRoute>
                <FreelancerProjects />
              </FreelancerRoute>
            } 
          />
          <Route 
            path="/freelancer/requests" 
            element={
              <FreelancerRoute>
                <FreelancerRequests />
              </FreelancerRoute>
            } 
          />
          <Route 
            path="/freelancer/projects/:projectId/upload" 
            element={
              <FreelancerRoute>
                <ProjectDetails />
              </FreelancerRoute>
            } 
          />
          <Route 
            path="/freelancer/projects/:projectId/messages" 
            element={
              <FreelancerRoute>
                <ProjectMessaging />
              </FreelancerRoute>
            } 
          />
          
          {/* Shared Project Routes */}
          <Route 
            path="/projects/:projectId/details" 
            element={
              <ProtectedRoute>
                <ProjectDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/messages" 
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/messages/:projectId" 
            element={
              <ProtectedRoute>
                <ProjectMessaging />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin Routes - Require Admin Role */}
          <Route 
            path="/admin" 
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/projects" 
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/projects/:projectId" 
            element={
              <AdminRoute>
                <ProjectDetails />
              </AdminRoute>
            } 
          />
          
          {/* Manager Routes - Require Manager or Admin Role */}
          <Route 
            path="/manager" 
            element={
              <ManagerRoute>
                <ManagerDashboard />
              </ManagerRoute>
            } 
          />
          <Route 
            path="/manager/projects" 
            element={
              <ManagerRoute>
                <ManagerDashboard />
              </ManagerRoute>
            } 
          />
          <Route 
            path="/manager/projects/:projectId" 
            element={
              <ManagerRoute>
                <ProjectDetails />
              </ManagerRoute>
            } 
          />
          
          {/* 404 Route - Catch All */}
          <Route 
            path="*" 
            element={
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                  <p className="text-gray-600 mb-8">Page not found</p>
                  <a 
                    href="/dashboard" 
                    className="btn btn-primary"
                  >
                    Go to Dashboard
                  </a>
                </div>
              </div>
            } 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;